import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { permForPath } from '@/lib/navPermissions';
import { useTheme } from '@/contexts/ThemeContext';
import { OnboardingTour } from '@/components/OnboardingTour';
import TrialBanner from '@/components/TrialBanner';
import MaintenanceBanner from '@/components/MaintenanceBanner';
import { api } from '@/lib/api';
import { resolveHelpSection } from '@/lib/helpRoutes';
import HelpPopover from '@/components/HelpPopover';
import SoundToggle from '@/components/SoundToggle';
import { getBranding, loadAndApplyBranding, reapplyBranding, getTerm, useTerms } from '@/lib/branding';
import HealthStatusBadge from '@/components/HealthStatusBadge';
import WorkspaceSwitcher from '@/components/WorkspaceSwitcher';
import WorkbenchDock from '@/components/WorkbenchDock';
import WorkbenchSwitcher from '@/components/WorkbenchSwitcher';
import { DiscussionThreadDialog } from '@/components/DiscussionDialog';
import { playChime } from '@/lib/chime';
import SplitView from '@/components/SplitView';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import ReleaseNotesDialog from '@/components/ReleaseNotesDialog';
import { RELEASE_VERSION } from '@/data/releaseNotes';
import AvatarCropDialog from '@/components/AvatarCropDialog';
import CollabButton from '@/components/collab/CollabButton';
import CollabOverlay from '@/components/collab/CollabOverlay';
import {
  BarChart3, LayoutDashboard, FileText, Database, MessageSquare,
  Settings, LogOut, Sun, Moon, ChevronLeft, ChevronRight, ChevronDown, Search, Bell,
  GitBranch, Shield, BookOpen, Clock, Server, Shuffle, Sparkles, Layers, HelpCircle, Building2, Loader2, Code, Briefcase, Inbox, Users, Ticket, UserCheck, X, Activity, Calendar, Store, GitPullRequest, Shapes, Plug, ShieldCheck, Globe, CreditCard, Lightbulb, Presentation, Lock, TrendingUp, Target, CalendarClock,
  BadgeCheck, Award, ShieldAlert, Share2, Gauge, FlaskConical, Radar, AlertOctagon, GraduationCap, Download,
} from 'lucide-react';
import { Package } from 'lucide-react';
import { Toaster } from '@/components/ui/sonner';
import { NavigationGuard } from '@/components/NavigationGuard';
import { canSee as canSeeRole } from '@/config/adminNav';
import FeedbackWidget from '@/components/FeedbackWidget';
import AdminReturnBar from '@/components/AdminReturnBar';
import PWAStatus from '@/components/PWAStatus';
import useInstallPrompt from '@/hooks/useInstallPrompt';
import CoachWidget from '@/coach/CoachWidget';
import StudioRecorderBar from '@/coach/StudioRecorderBar';
import useActivityHeartbeat from '@/hooks/useActivityHeartbeat';

// Primary navigation — organized around user goals (Analyze → Decide →
// Monitor → Govern → Build), per the Main Application Navigation Restructure.
// Routes are preserved; several tools now live as tabs inside their parent
// work area (Sentinels, Recommendations, Investigations) and are reachable by
// deep link. Access is capability-driven via navPermissions.PATH_PERMISSIONS.
const navItems = [
  { path: '/dashboard', label: 'Home', icon: LayoutDashboard, termKey: 'home' },
  { type: 'group', label: 'ANALYZE', icon: BarChart3, children: [
    { path: '/ai-copilot', label: 'Ask AI', icon: Search, termKey: 'ai_explore' },
    { path: '/dashboards', label: 'Dashboards & Reports', icon: FileText, termKey: 'insights' },
    { path: '/investigations', label: 'Investigations', icon: Activity },
    { path: '/executive-summaries', label: 'Executive Briefings', icon: Presentation },
    { path: '/visual-library', label: 'Visual Library', icon: Layers, termKey: 'visual_library' },
  ]},
  { type: 'group', label: 'DECISIONS', icon: Target, children: [
    { path: '/my-work', label: 'My Work', icon: Inbox },
    { path: '/initiatives', label: 'Initiatives', icon: Layers },
    { path: '/objectives', label: 'Goals and Guardrails', icon: Target },
    { path: '/results', label: 'Results', icon: Activity },
    { path: '/admin/retention-preview', label: 'Retention', icon: ShieldAlert },
    { path: '/admin/scenario-simulator', label: 'What-if Analysis', icon: FlaskConical },
    { path: '/decision-policies', label: 'Policies', icon: Shield },
    { path: '/ai-insights', label: 'Findings', icon: Lightbulb, termKey: 'ai_insights' },
  ]},
  { type: 'group', label: 'MONITOR', icon: Radar, children: [
    { path: '/kpi-monitor', label: 'KPI Monitor', icon: BarChart3, termKey: 'kpi_monitor' },
    { path: '/admin/sentinels', label: 'Sentinels', icon: Radar },
    { path: '/admin/incidents', label: 'Incidents', icon: AlertOctagon },
    { path: '/alerts', label: 'Alerts', icon: Bell },
    { path: '/scheduling', label: 'Schedules', icon: Clock },
  ]},
  { type: 'group', label: 'GOVERNANCE', icon: ShieldCheck, children: [
    { path: '/governance', label: 'Governance Overview', icon: ShieldCheck },
    { path: '/admin/adoption-analytics', label: 'Adoption Analytics', icon: TrendingUp },
    { path: '/admin/certification-queue', label: 'Certification Queue', icon: Award },
    { path: '/admin/metric-registry', label: 'Metric Registry', icon: BadgeCheck },
    { path: '/admin/calculation-packs', label: 'Calculation Packs', icon: Package },
    { path: '/admin/data-quality', label: 'Data Quality', icon: ShieldAlert },
    { path: '/admin/catalog', label: 'Data Catalog', icon: Database },
    { path: '/lineage-explorer', label: 'Lineage', icon: Share2 },
    { path: '/ai-coverage', label: 'AI Readiness', icon: Sparkles },
    { path: '/admin/explore-eval', label: 'AI Quality', icon: Gauge },
    { path: '/admin/pii-compliance', label: 'PII & Data Access', icon: Shield },
    { path: '/admin/explore-kpis', label: 'Governance Analytics', icon: TrendingUp },
    { path: '/change-requests', label: 'Change Requests', icon: GitPullRequest },
    { path: '/coach-studio', label: 'Coach Studio', icon: GraduationCap, role: 'admin' },
  ]},
  { type: 'group', label: 'DATA', icon: Database, children: [
    { path: '/data-sources', label: 'Sources', icon: Database, termKey: 'sources' },
    { path: '/data-prep', label: 'Data Prep', icon: Shuffle },
    { path: '/semantic-models', label: 'Models', icon: GitBranch },
    { path: '/glossary', label: 'Glossary', icon: BookOpen },
  ]},
  { type: 'group', label: 'COLLABORATE', icon: MessageSquare, children: [
    { path: '/threads', label: 'Discussions', icon: MessageSquare, termKey: 'discussions' },
    { path: '/my-feedback', label: 'My Feedback', icon: Inbox },
    { path: '/activity', label: 'Activity', icon: Activity },
  ]},
  { type: 'group', label: 'APPS', icon: Store, children: [
    { path: '/apps', label: 'Apps', icon: Layers, termKey: 'apps' },
    { path: '/marketplace', label: 'Marketplace', icon: Store, termKey: 'marketplace' },
  ]},
  { type: 'group', label: 'DEVELOPER', icon: Code, children: [
    { path: '/developers', label: 'API Reference', icon: Code, role: 'admin' },
    { path: '/sdk/visuals', label: 'Custom Visuals', icon: Shapes },
    { path: '/sdk/connectors', label: 'Custom Connectors', icon: Plug },
    { path: '/embed-manager', label: 'Embed SDK', icon: Globe },
    { path: '/developer-docs', label: 'Documentation', icon: BookOpen, external: true },
  ]},
  { path: '/administration', label: 'Administration', icon: Shield },
  { path: '/platform-console', label: 'Platform Console', icon: Server },
];

export default function Layout() {
  const { user, logout, hasPermission, permsLoaded, setUserData } = useAuth();
  // Live-presence heartbeat so admins can see who is online + what page.
  useActivityHeartbeat(!!user);
  // A nav item is visible only if the role check passes AND the user has the
  // permission the target page requires. While permissions are still loading we
  // don't hide anything (avoids a flash of an empty menu).
  const allow = (path, role) => {
    if (role && !canSeeRole(role, user)) return false;
    if (!permsLoaded) return true;
    const p = permForPath(path);
    return !p || hasPermission(p);
  };
  const { theme, toggleTheme, availableThemes, setTheme } = useTheme();
  const { canInstall, install } = useInstallPrompt();
  const navigate = useNavigate();
  const ws = useWorkspace();
  useTerms(); // re-render nav labels when branding terminology changes
  const navLabel = (item) => (item.termKey ? getTerm(item.termKey, 'plural') : item.label);
  const [brand, setBrand] = React.useState(getBranding());
  React.useEffect(() => {
    loadAndApplyBranding();
    const onUpd = (e) => setBrand(e.detail || getBranding());
    window.addEventListener('branding:updated', onUpd);
    return () => window.removeEventListener('branding:updated', onUpd);
  }, []);
  // Re-apply the mode-appropriate logo/colors whenever the theme changes.
  React.useEffect(() => { reapplyBranding(theme === 'dark'); }, [theme]);
  const location = useLocation();
  // Split view is a session-only overlay of two panes. Navigating the app via
  // the sidebar must exit it, otherwise the split stays mounted and left-nav
  // clicks appear to do nothing.
  const splitActive = !!ws?.split;
  const clearSplit = ws?.clearSplit;
  useEffect(() => {
    if (splitActive && clearSplit) clearSplit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Floating discussion: opened from a thread's "Open Insight/Explore/Report"
  // so the user can view the resource and keep replying in a draggable panel.
  const [floatingDiscussionId, setFloatingDiscussionId] = useState(null);
  useEffect(() => {
    const onOpen = (e) => setFloatingDiscussionId(e.detail?.discussionId || null);
    window.addEventListener('discussion:open-floating', onOpen);
    return () => window.removeEventListener('discussion:open-floating', onOpen);
  }, []);

  // Background AI Explore answers: chime + toast when an answer lands while the
  // user is elsewhere, so they can work in parallel and jump back when ready.
  useEffect(() => {
    const onReady = (e) => {
      const d = e.detail || {};
      const onThatSession = location.pathname === '/ai-copilot' && document.hasFocus();
      if (onThatSession) return; // actively on AI Explore — answer renders inline, no toast/chime
      playChime();
      const q = (d.question || 'Your question').slice(0, 60);
      toast.success('AI Explore answer ready', {
        description: q,
        action: {
          label: 'View',
          onClick: () => navigate(`/ai-copilot?model=${d.model_id}${d.session ? `&s=${d.session}` : ''}`),
        },
      });
    };
    window.addEventListener('explore:answer-ready', onReady);
    return () => window.removeEventListener('explore:answer-ready', onReady);
  }, [location.pathname, location.search, navigate]);

  const [collapsed, setCollapsed] = useState(false);
  // When collapsed, a group's children are shown in a hover flyout anchored to
  // the group icon (the inline accordion is hidden at w-16). Fixed-positioned so
  // the nav's vertical scroll container can't clip it.
  const [flyout, setFlyout] = useState(null); // { item, top }
  const flyoutTimer = useRef(null);
  const openFlyout = (item, e) => {
    if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setFlyout({ item, top: rect.top });
  };
  const scheduleCloseFlyout = () => {
    if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
    flyoutTimer.current = setTimeout(() => setFlyout(null), 150);
  };
  const cancelCloseFlyout = () => { if (flyoutTimer.current) clearTimeout(flyoutTimer.current); };
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [liveVersion, setLiveVersion] = useState(null);
  const [buildInfo, setBuildInfo] = useState(null);
  useEffect(() => {
    let active = true;
    api.getPlatformVersion()
      .then((v) => { if (active && v?.version) { setLiveVersion(String(v.version).replace(/^v/i, '')); setBuildInfo(v); } })
      .catch(() => {});
    return () => { active = false; };
  }, []);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({ ANALYZE: true, DECISIONS: false, MONITOR: false, GOVERNANCE: false, DATA: false, COLLABORATE: false, APPS: false, DEVELOPER: false });
  const [notifications, setNotifications] = useState([]);
  const [discussionUnread, setDiscussionUnread] = useState(0);
  const [myFeedbackUnread, setMyFeedbackUnread] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', phone: '', title: '', email: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', newPass: '', confirm: '' });
  const unreadCount = notifications.filter(n => !n.read).length;

  // Auto-expand the group that contains the active route (e.g. deep-linking to
  // /admin/sentinels opens MONITOR) — never collapses groups the user opened.
  useEffect(() => {
    const g = navItems.find(it => it.type === 'group' && (it.children || []).some(
      c => location.pathname === c.path || location.pathname.startsWith(c.path + '/')));
    if (g) setExpandedGroups(prev => (prev[g.label] ? prev : { ...prev, [g.label]: true }));
  }, [location.pathname]);

  useEffect(() => {
    if (user) api.getMessagingUnread().then(r => setDiscussionUnread(r?.unread || 0)).catch(() => {});
  }, [location.pathname, user]);

  // My Feedback unread indicator — refresh on navigation. Opening the page sets
  // my_feedback_last_seen (see MyFeedbackPage), so re-fetching on route change
  // clears the dot when the user leaves the page.
  const refreshMyFeedbackUnread = useCallback(() => {
    if (user) api.getMyFeedbackUnread().then(r => setMyFeedbackUnread(r?.count || 0)).catch(() => {});
  }, [user]);
  useEffect(() => { refreshMyFeedbackUnread(); }, [refreshMyFeedbackUnread, location.pathname]);
  useEffect(() => {
    const h = () => setMyFeedbackUnread(0);
    window.addEventListener('my-feedback-seen', h);
    return () => window.removeEventListener('my-feedback-seen', h);
  }, []);

  useEffect(() => {
    if (user) {
      api.getNotificationInbox().then(setNotifications).catch(() => {});
      api.getMessagingUnread().then(r => setDiscussionUnread(r?.unread || 0)).catch(() => {});
      import('@/lib/messagingSocket').then(m => {
        m.connectMessaging();
        m.onMessagingEvent(evt => {
          if (evt.type === 'message') {
            if (evt.message && evt.message.sender_id !== user?.user_id) playChime();
            api.getMessagingUnread().then(r => setDiscussionUnread(r?.unread || 0)).catch(() => {});
          }
        });
      }).catch(() => {});
      // Register tenant-authored custom visuals (SDK) into the viz registry.
      import('@/lib/customVisualLoader').then(m => m.loadTenantCustomVisuals()).catch(() => {});
      // Register installed marketplace visual extensions (radar, calendar heatmap, gantt).
      import('@/lib/marketplaceVisualLoader').then(m => m.loadInstalledMarketplaceVisuals()).catch(() => {});
    }
  }, [user]);
  // First-run product tour: show ONLY on the dashboard/home — its highlights
  // point at the primary nav/header, so it must never overlay a deep page (e.g.
  // /admin/sentinels) where its tooltip would intercept row/action clicks.
  // Auto-dismiss the moment the user navigates off the dashboard.
  useEffect(() => {
    if (!user) return;
    const onDashboard = location.pathname === '/dashboard';
    if (onDashboard && !localStorage.getItem('liquid_onboarding_done')) setShowOnboarding(true);
    else if (!onDashboard) setShowOnboarding(false);
  }, [user, location.pathname]);
  // Poll the notification inbox so drift alerts / governance notices surface
  // without a page reload.
  useEffect(() => {
    if (!user) return;
    const id = setInterval(() => {
      api.getNotificationInbox().then(setNotifications).catch(() => {});
    }, 60000);
    return () => clearInterval(id);
  }, [user]);
  // Chime when the number of unread notifications increases.
  const prevUnreadRef = React.useRef(null);
  useEffect(() => {
    if (prevUnreadRef.current !== null && unreadCount > prevUnreadRef.current) playChime();
    prevUnreadRef.current = unreadCount;
  }, [unreadCount]);
  // Re-sync marketplace visual types when an extension is installed/uninstalled.
  useEffect(() => {
    const resync = () => import('@/lib/marketplaceVisualLoader')
      .then(m => m.loadInstalledMarketplaceVisuals()).catch(() => {});
    window.addEventListener('marketplace-changed', resync);
    return () => window.removeEventListener('marketplace-changed', resync);
  }, []);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [aiSearching, setAiSearching] = useState(false);
  const [aiSearchResult, setAiSearchResult] = useState(null);
  const searchInputRef = React.useRef(null);

  const handleSearch = useCallback(async (q) => {
    setSearchQuery(q);
    setAiSearchResult(null);
    if (q.length < 2) { setSearchResults([]); setShowResults(false); return; }
    try {
      const res = await api.globalSearch(q);
      setSearchResults(res.results || []);
      setShowResults(true);
    } catch { setSearchResults([]); }
  }, []);

  const handleAiSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setAiSearching(true);
    setAiSearchResult(null);
    try {
      // Use the SAME model AI Explore is set to (persisted) so results are
      // consistent with asking there directly; fall back to published/first.
      const models = await api.getSemanticModels();
      const stored = localStorage.getItem('inzara.exploreModel');
      const model = models.find(m => m.model_id === stored) || models.find(m => m.status === 'published') || models[0];
      if (!model) { toast.error('No semantic models available'); return; }
      const res = await api.exploreAsk({ question: searchQuery, session_id: '', model_id: model.model_id });
      setAiSearchResult({ result: res.result, model_id: model.model_id, model_name: model.name });
    } catch (err) { toast.error(err.message); }
    finally { setAiSearching(false); }
  }, [searchQuery]);

  // Cmd+/ shortcut to focus the top "Ask AI" search bar.
  // (Cmd+K is owned by the global Command Palette — see CommandPalette.jsx.)
  React.useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') { e.preventDefault(); searchInputRef.current?.focus(); setShowResults(true); }
      if (e.key === 'Escape') { setShowResults(false); setAiSearchResult(null); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const navigateToResult = (result) => {
    const routes = { dashboard: '/dashboards/view?id=' + (result.id || ''), report: '/dashboards/view?id=' + (result.id || ''), visual: '/visual-library/edit?id=' + (result.id || ''), glossary: '/glossary', semantic_entity: '/semantic-models', data_source: '/data-sources', alert: '/alerts' };
    navigate(routes[result.type] || '/dashboard');
    setShowResults(false);
    setSearchQuery('');
    setAiSearchResult(null);
  };

  const handleLogout = async () => {
    const slug = brand?.login_slug;
    await logout();
    // If a branded login page is configured, send users there on logout.
    if (slug) { window.location.assign(`/${slug}`); return; }
    navigate('/login');
  };

  // Chromeless mode — used by Split View iframes to render just the page
  // content (no sidebar/header/tab-strip), so two resources fit side-by-side.
  const chromeless = new URLSearchParams(location.search).get('chromeless') === '1';
  const isRefPane = new URLSearchParams(location.search).get('ref') === '1';
  if (chromeless) {
    return (
      <div className="h-screen w-screen overflow-auto bg-background" data-testid="chromeless-shell">
        {isRefPane && (
          <div className="sticky top-0 z-50 px-3 py-1 text-[10px] font-medium text-amber-400 bg-amber-500/10 border-b border-amber-500/30 text-center" data-testid="ref-pane-ribbon">
            Reference view — open this in its own tab to make edits
          </div>
        )}
        <Outlet />
        <Toaster position="top-right" richColors />
        <NavigationGuard />
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background" data-testid="app-layout">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`relative flex-col border-r border-border/50 transition-all duration-300 hidden md:flex ${collapsed ? 'w-16' : 'w-60'}`}
        style={{ background: 'var(--sidebar-bg)', backdropFilter: 'blur(24px)' }}
      >
        {/* Logo */}
        <div className={`flex items-center gap-3 px-3 py-3 border-b border-border/50 ${collapsed ? 'justify-center' : ''} cursor-pointer`} onClick={() => navigate('/dashboard')} data-testid="logo-home-link">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={brand.app_name || 'Logo'} className={collapsed ? 'h-8 w-8 object-contain' : 'max-h-12 object-contain px-1'} data-testid="brand-logo" />
          ) : (brand.app_name && brand.app_name !== 'INZARA') ? (
            <span className={collapsed ? 'text-base font-heading font-bold text-foreground' : 'text-xl font-heading font-bold text-foreground truncate px-1'} data-testid="brand-wordmark" title={brand.app_name}>
              {collapsed ? brand.app_name.trim().charAt(0).toUpperCase() : brand.app_name}
            </span>
          ) : (
            <img src={collapsed ? '/inzara-icon.png' : (theme === 'dark' ? '/inzara-dark.png' : '/inzara-light.png')} alt={brand.app_name || 'INZARA'} className={collapsed ? 'h-8 w-8 object-contain' : 'w-full max-h-16 object-contain px-1'} />
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 min-h-0 py-3 px-2 space-y-1 overflow-y-auto">
          {navItems.map((item, idx) => {
            if (item.role && !canSeeRole(item.role, user)) return null;
            if (item.type === 'group') {
              const visibleChildren = item.children.filter(child => allow(child.path, child.role));
              if (visibleChildren.length === 0) return null;
              const isExpanded = expandedGroups[item.label];
              const childActive = item.children.some(c => location.pathname.startsWith(c.path));
              return (
                <div key={item.label}
                  onMouseEnter={collapsed ? (e) => openFlyout(item, e) : undefined}
                  onMouseLeave={collapsed ? scheduleCloseFlyout : undefined}
                >
                  <button
                    onClick={() => setExpandedGroups(prev => ({ ...prev, [item.label]: !prev[item.label] }))}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200
                      ${childActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}
                      ${collapsed ? 'justify-center' : ''}`}
                    aria-label={navLabel(item)}
                    aria-expanded={isExpanded}
                    data-testid={`nav-group-${item.label.toLowerCase()}`}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                    {!collapsed && <>
                      <span className="flex-1 text-left">{navLabel(item)}</span>
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`} />
                    </>}
                  </button>
                  {isExpanded && !collapsed && (
                    <div className="ml-4 pl-3 border-l border-border/30 space-y-0.5 mt-0.5 mb-1">
                      {visibleChildren.map(child => {
                        // Use exact match for any child that has another sibling whose path starts with `${child.path}/`
                        // Prevents e.g. /admin from staying highlighted while on /admin/jobs.
                        const hasDescendantSibling = item.children.some(c => c.path !== child.path && c.path.startsWith(child.path + '/'));
                        if (child.external) {
                          return (
                            <a key={child.path} href={child.path} target="_blank" rel="noreferrer" data-testid={`nav-${child.path.slice(1)}`}
                              className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-medium transition-all duration-200 text-muted-foreground hover:text-foreground hover:bg-muted/50">
                              <child.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                              <span>{navLabel(child)}</span>
                            </a>
                          );
                        }
                        return (
                          <NavLink key={child.path} to={child.path} end={hasDescendantSibling} data-testid={`nav-${child.path.slice(1)}`}
                            data-coach-target={child.path === '/dashboards' ? 'dashboards-nav' : child.path === '/alerts' ? 'alerts-nav' : `nav-${child.path.slice(1)}`}
                            className={({ isActive }) => `flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-medium transition-all duration-200 ${isActive ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}>
                            <child.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                            <span className="flex-1">{navLabel(child)}</span>
                            {child.path === '/my-feedback' && myFeedbackUnread > 0 && (
                              <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="nav-my-feedback-unread">{myFeedbackUnread}</span>
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            if (!allow(item.path, item.role)) return null;
            // Same exact-match logic for top-level items: if any other item is a descendant
            // path of this one, use `end` so this item only highlights on exact path match.
            const hasDescendantSibling = navItems.some(other =>
              other !== item && (other.path || '').startsWith(item.path + '/'));
            return (
              <NavLink key={item.path} to={item.path} end={hasDescendantSibling} data-testid={`nav-${item.path.slice(1)}`} aria-label={navLabel(item)}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 active:scale-95 ${isActive ? 'bg-primary/15 text-primary border border-primary/20 shadow-[0_0_12px_rgba(59,130,246,0.15)]' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'} ${collapsed ? 'justify-center' : ''}`}>
                <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                {!collapsed && <span className="flex-1">{navLabel(item)}</span>}
                {item.path === '/threads' && discussionUnread > 0 && (
                  <span className={`${collapsed ? 'absolute top-1 right-1' : ''} min-w-[18px] h-[18px] px-1 rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center`} data-testid="nav-discussions-unread">{discussionUnread}</span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Collapsed-sidebar flyout: shows the hovered group's submenu items to
            the right of the rail. Fixed-positioned to escape the nav scroll clip. */}
        {collapsed && flyout && (
          <div
            className="fixed z-[200] left-16 animate-in fade-in slide-in-from-left-1 duration-150"
            style={{ top: flyout.top }}
            onMouseEnter={cancelCloseFlyout}
            onMouseLeave={scheduleCloseFlyout}
            data-testid={`nav-flyout-${flyout.item.label.toLowerCase()}`}
          >
            <div className="ml-1 min-w-[200px] max-h-[70vh] overflow-y-auto bg-popover text-popover-foreground border border-border/50 rounded-xl shadow-2xl p-2">
              <div className="px-2 py-1 mb-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{navLabel(flyout.item)}</div>
              {flyout.item.children.filter(child => allow(child.path, child.role)).map(child => {
                const hasDescendantSibling = flyout.item.children.some(c => c.path !== child.path && c.path.startsWith(child.path + '/'));
                if (child.external) {
                  return (
                    <a key={child.path} href={child.path} target="_blank" rel="noreferrer" data-testid={`nav-flyout-${child.path.slice(1)}`}
                      className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-medium transition-all duration-200 text-muted-foreground hover:text-foreground hover:bg-muted/50">
                      <child.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                      <span>{navLabel(child)}</span>
                    </a>
                  );
                }
                return (
                  <NavLink key={child.path} to={child.path} end={hasDescendantSibling} onClick={() => setFlyout(null)} data-testid={`nav-flyout-${child.path.slice(1)}`}
                    className={({ isActive }) => `flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-medium transition-all duration-200 ${isActive ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}>
                    <child.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                    <span>{navLabel(child)}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        )}

        {/* Bottom */}
        <div className="shrink-0 p-3 border-t border-border/50 space-y-2">
          {!collapsed && user && (
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg mb-1 cursor-pointer hover:bg-muted/30 transition-colors relative" onClick={() => { setProfileForm({ name: user.name || '', phone: user.phone || '', title: user.title || '', email: user.email || '', avatarUrl: user.avatarUrl || '', ai_persona: user.ai_persona || '' }); setProfileOpen(!profileOpen); }} title="Profile" data-testid="profile-trigger">
              <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0 overflow-hidden">
                {user.avatarUrl ? <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" /> : (user.name?.[0]?.toUpperCase() || 'U')}
              </div>
              <div className="overflow-hidden flex-1">
                <p className="text-xs font-medium text-foreground truncate">{user.name}</p>
                <p className="text-[10px] text-muted-foreground truncate">{user.title || user.role}</p>
              </div>
              <CollabButton />
              {canInstall && (
                <button
                  type="button"
                  data-testid="sidebar-install-btn"
                  onClick={(e) => { e.stopPropagation(); install(); }}
                  title="Install app"
                  aria-label="Install app"
                  className="flex-shrink-0 p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                >
                  <Download className="w-4 h-4" strokeWidth={1.5} />
                </button>
              )}
            </div>
          )}
          {/* Profile Popup — portaled to <body> so it escapes the sidebar's
              backdrop-filter stacking context (otherwise glass-card panels
              in the main content render ON TOP of the popup). */}
          {profileOpen && createPortal(
            <>
              <div className="fixed inset-0 z-[199]" onClick={() => setProfileOpen(false)} />
              <div className="fixed bottom-4 left-4 w-80 rounded-2xl shadow-2xl border border-border/50 z-[200] overflow-hidden bg-background" data-testid="profile-popup">
              <div className="p-4 border-b border-border/30 flex items-center gap-3 relative">
                {/* Close button — top-right */}
                <button
                  onClick={() => setProfileOpen(false)}
                  className="absolute top-2 right-2 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
                  data-testid="profile-close-btn"
                  aria-label="Close profile"
                  title="Close"
                >
                  <X className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
                <label className="relative cursor-pointer group" title="Click to change avatar">
                  <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center text-xl font-bold text-primary flex-shrink-0 group-hover:bg-primary/30 transition-colors overflow-hidden">
                    {profileForm.avatarUrl ? <img src={profileForm.avatarUrl} alt="" className="w-full h-full object-cover" /> : (user?.name?.[0]?.toUpperCase() || 'U')}
                  </div>
                  <div className="absolute inset-0 rounded-full flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"><span className="text-[8px] text-white font-medium">Change</span></div>
                  <input type="file" accept="image/*" className="hidden" data-testid="profile-avatar-input" onChange={e => { const f = e.target.files?.[0]; if (f) { const r = new FileReader(); r.onload = () => setCropSrc(r.result); r.readAsDataURL(f); } e.target.value = ''; }} />
                </label>
                <div className="pr-5">
                  <p className="text-sm font-heading font-semibold text-foreground">{user?.name}</p>
                  <p className="text-[10px] text-muted-foreground">{user?.email}</p>
                  <p className="text-[9px] text-primary/60 capitalize mt-0.5">{user?.role}</p>
                  {profileForm.avatarUrl && (
                    <button type="button" data-testid="profile-remove-photo"
                      onClick={async () => { try { const updated = await api.updateProfile({ avatarUrl: '' }); setProfileForm(p => ({ ...p, avatarUrl: '' })); if (updated && setUserData) setUserData(updated); toast.success('Photo removed'); } catch (err) { toast.error(err.message); } }}
                      className="text-[9px] text-rose-400 hover:text-rose-300 mt-1 inline-flex items-center gap-0.5">
                      <X className="w-2.5 h-2.5" /> Remove photo
                    </button>
                  )}
                </div>
              </div>
              <div className="p-4 space-y-2.5 max-h-72 overflow-auto">
                <div><label className="text-[9px] text-muted-foreground uppercase tracking-wider">Name</label><input value={profileForm.name} onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))} className="w-full mt-0.5 h-7 px-2 rounded-lg bg-muted/30 border border-border/30 text-xs text-foreground outline-none focus:border-primary/40" /></div>
                <div><label className="text-[9px] text-muted-foreground uppercase tracking-wider">Title / Position</label><input value={profileForm.title} onChange={e => setProfileForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Senior Analyst" className="w-full mt-0.5 h-7 px-2 rounded-lg bg-muted/30 border border-border/30 text-xs text-foreground outline-none focus:border-primary/40" /></div>
                <div><label className="text-[9px] text-muted-foreground uppercase tracking-wider">Phone</label><input value={profileForm.phone} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))} placeholder="+1 (555) 000-0000" className="w-full mt-0.5 h-7 px-2 rounded-lg bg-muted/30 border border-border/30 text-xs text-foreground outline-none focus:border-primary/40" /></div>
                <div><label className="text-[9px] text-muted-foreground uppercase tracking-wider">AI Persona (for AI Explore)</label><textarea value={profileForm.ai_persona || ''} onChange={e => setProfileForm(p => ({ ...p, ai_persona: e.target.value }))} placeholder="e.g. Focus on personal-lines underwriting; prefer loss ratio; explain in plain language." rows={3} className="w-full mt-0.5 px-2 py-1.5 rounded-lg bg-muted/30 border border-border/30 text-xs text-foreground outline-none focus:border-primary/40 resize-none" data-testid="profile-ai-persona" /><p className="text-[9px] text-muted-foreground/70 mt-0.5">Layered on top of your role's persona and sent to AI Explore.</p></div>
                <div><label className="text-[9px] text-muted-foreground uppercase tracking-wider">Email</label><input type="email" value={profileForm.email || ''} onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))} placeholder="you@company.com" className="w-full mt-0.5 h-7 px-2 rounded-lg bg-muted/30 border border-border/30 text-xs text-foreground outline-none focus:border-primary/40" data-testid="profile-email" /></div>
                <div><label className="text-[9px] text-muted-foreground uppercase tracking-wider">Tenant</label><p className="text-xs text-foreground/70 mt-0.5" data-testid="profile-tenant-name">{user?.tenant_name || user?.tenant_id}</p></div>
                <button onClick={async () => {
                  setProfileSaving(true);
                  try { const updated = await api.updateProfile({ ...profileForm, avatarUrl: profileForm.avatarUrl }); if (updated && setUserData) setUserData(updated); toast.success('Profile updated'); setProfileOpen(false); } catch (err) { toast.error(err.message); }
                  finally { setProfileSaving(false); }
                }} disabled={profileSaving} className="w-full h-8 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors" data-testid="profile-save-btn">
                  {profileSaving ? 'Saving...' : 'Save Profile'}
                </button>
                {/* Change password — collapsed by default to keep the popup compact. */}
                <div className="border-t border-border/20 pt-2">
                  <button
                    type="button"
                    onClick={() => { setPwOpen(o => !o); setPwForm({ current: '', next: '', confirm: '' }); }}
                    data-testid="change-pw-toggle"
                    className="w-full flex items-center justify-between text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <span className="uppercase tracking-wider">Change password</span>
                    <span className="text-foreground/70">{pwOpen ? '−' : '+'}</span>
                  </button>
                  {pwOpen && (
                    <div className="mt-2 space-y-2" data-testid="change-pw-form">
                      <input type="password" value={pwForm.current} onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))} placeholder="Current password" autoComplete="current-password" data-testid="pw-current" className="w-full h-7 px-2 rounded-lg bg-muted/30 border border-border/30 text-xs text-foreground outline-none focus:border-primary/40" />
                      <input type="password" value={pwForm.next} onChange={e => setPwForm(p => ({ ...p, next: e.target.value }))} placeholder="New password (min 10 chars)" autoComplete="new-password" data-testid="pw-next" className="w-full h-7 px-2 rounded-lg bg-muted/30 border border-border/30 text-xs text-foreground outline-none focus:border-primary/40" />
                      <input type="password" value={pwForm.confirm} onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))} placeholder="Confirm new password" autoComplete="new-password" data-testid="pw-confirm" className="w-full h-7 px-2 rounded-lg bg-muted/30 border border-border/30 text-xs text-foreground outline-none focus:border-primary/40" />
                      <button
                        onClick={async () => {
                          if (!pwForm.current || !pwForm.next) { toast.error('Fill in current and new passwords'); return; }
                          if (pwForm.next !== pwForm.confirm) { toast.error('New passwords do not match'); return; }
                          if (pwForm.next === pwForm.current) { toast.error('New password must differ from current'); return; }
                          setPwSaving(true);
                          try {
                            await api.changePassword(pwForm.current, pwForm.next);
                            toast.success('Password changed');
                            setPwForm({ current: '', next: '', confirm: '' });
                            setPwOpen(false);
                          } catch (err) {
                            toast.error(err.message || 'Failed to change password');
                          } finally { setPwSaving(false); }
                        }}
                        disabled={pwSaving}
                        data-testid="pw-save-btn"
                        className="w-full h-7 rounded-lg bg-primary/90 text-primary-foreground text-xs font-medium hover:bg-primary disabled:opacity-50 transition-colors"
                      >
                        {pwSaving ? 'Updating...' : 'Update password'}
                      </button>
                    </div>
                  )}
                </div>
                <div className="border-t border-border/20 pt-2 flex justify-between items-center">
                  <button onClick={() => { setProfileOpen(false); navigate('/settings'); }} className="text-[10px] text-muted-foreground hover:text-foreground transition-colors">All Settings</button>
                  <button
                    onClick={() => {
                      if (user?.email) localStorage.removeItem(`li-first-login-${user.email.toLowerCase()}`);
                      setProfileOpen(false);
                      toast.success('Onboarding reset — showing the sample gallery');
                      navigate('/marketplace');
                    }}
                    className="text-[10px] text-muted-foreground hover:text-primary transition-colors"
                    data-testid="reset-first-run-btn"
                  >Reset to first-run view</button>
                  <button onClick={() => { setProfileOpen(false); handleLogout(); }} className="text-[10px] text-destructive/70 hover:text-destructive transition-colors">Sign Out</button>
                </div>
              </div>
            </div>
            </>,
            document.body
          )}
          <AvatarCropDialog
            open={!!cropSrc}
            imageSrc={cropSrc}
            onCancel={() => setCropSrc(null)}
            onCropped={async (blob) => {
              try {
                const res = await api.uploadAvatar(blob);
                setProfileForm(p => ({ ...p, avatarUrl: res.avatarUrl }));
                if (user && setUserData) setUserData({ ...user, avatarUrl: res.avatarUrl });
                toast.success('Photo updated');
              } catch (err) {
                toast.error(err.message || 'Could not upload photo');
              } finally {
                setCropSrc(null);
              }
            }}
          />

          <Button
            data-testid="nav-help"
            variant="ghost"
            size="sm"
            onClick={() => navigate('/help')}
            aria-label="Help"
            className={`w-full text-muted-foreground hover:text-primary rounded-lg ${collapsed ? 'px-0 justify-center' : 'justify-start'}`}
          >
            <HelpCircle className="w-4 h-4" strokeWidth={1.5} />
            {!collapsed && <span className="ml-2 text-xs">Help</span>}
          </Button>
          <Button
            data-testid="logout-btn"
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            aria-label="Sign Out"
            className={`w-full text-muted-foreground hover:text-destructive rounded-lg ${collapsed ? 'px-0 justify-center' : 'justify-start'}`}
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            {!collapsed && <span className="ml-2 text-xs">Sign Out</span>}
          </Button>
          <button
            type="button"
            onClick={() => setReleaseOpen(true)}
            title="View latest release notes"
            data-testid="release-version-link"
            className={`w-full flex items-center gap-1.5 text-[10px] text-muted-foreground/80 hover:text-primary transition-colors ${collapsed ? 'justify-center' : 'justify-center'}`}
          >
            <Sparkles className="w-3 h-3 flex-shrink-0" />
            {!collapsed && <span>{brand.app_name || 'INZARA'} release v{liveVersion || RELEASE_VERSION}</span>}
          </button>
        </div>

        {/* Collapse toggle */}
        <button
          data-testid="sidebar-toggle"
          onClick={() => { setCollapsed(c => !c); setFlyout(null); setTimeout(() => window.dispatchEvent(new Event('resize')), 320); }}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="absolute -right-3 top-8 w-6 h-6 rounded-full bg-background border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors z-10"
        >
          {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>
      </aside>

      {/* Onboarding Tour */}
      {showOnboarding && <OnboardingTour onComplete={() => { setShowOnboarding(false); localStorage.setItem('liquid_onboarding_done', 'true'); }} />}
      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Trial countdown / read-only mode banner */}
        <TrialBanner />
        {/* System-update maintenance banner */}
        <MaintenanceBanner />
        {/* Impersonation banner */}
        {user?.impersonated_by && (
          <div className="px-4 py-2.5 bg-red-600 flex items-center justify-between" data-testid="impersonation-banner">
            <div className="flex items-center gap-2 text-sm text-white font-medium">
              <Shield className="w-4 h-4" />
              <span>You are impersonating <strong>{user.name || user.email}</strong> (started by {user.impersonated_by})</span>
            </div>
            <button onClick={async () => {
              const stored = localStorage.getItem('impersonating');
              // Return the admin to the screen they launched impersonation from.
              let returnTo = '/dashboard';
              if (stored) {
                try {
                  const parsed = JSON.parse(stored);
                  if (parsed.return_to) returnTo = parsed.return_to;
                  // End impersonation - backend will restore admin cookie via end-impersonation endpoint
                  await fetch(process.env.REACT_APP_BACKEND_URL + '/api/auth/end-impersonation', { method: 'POST', credentials: 'include' });
                  localStorage.removeItem('impersonating');
                } catch { /* ignore — fall through to logout */ }
              } else {
                await fetch(process.env.REACT_APP_BACKEND_URL + '/api/auth/logout', { method: 'POST', credentials: 'include' });
                localStorage.removeItem('impersonating');
              }
              window.location.href = returnTo;
            }} className="text-xs text-white font-semibold px-4 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors" data-testid="stop-impersonation-btn">
              Exit Impersonation
            </button>
          </div>
        )}
        {/* Top bar */}
        <header className="relative z-50 flex items-center justify-between px-6 py-3 border-b border-border/50" style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)' }}>
          <div className="flex items-center gap-3 flex-1 max-w-lg">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                ref={searchInputRef}
                data-testid="global-search"
                type="text"
                placeholder="Search or ask a question... (⌘/)"
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAiSearch(); } }}
                onFocus={() => setShowResults(true)}
                onBlur={() => setTimeout(() => { if (!aiSearchResult) setShowResults(false); }, 300)}
                className="w-full pl-10 pr-20 py-2 rounded-xl bg-muted/50 border border-border/50 text-sm text-foreground placeholder:text-muted-foreground/80 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-all"
              />
              {/* AI Search button inside input */}
              <button onClick={handleAiSearch} disabled={aiSearching || !searchQuery.trim()} aria-label="Ask AI" className="absolute right-2 top-1/2 -translate-y-1/2 px-2.5 py-1 rounded-lg bg-indigo-600 text-white text-[11px] font-semibold shadow-sm hover:bg-indigo-700 transition-colors disabled:bg-indigo-400 disabled:opacity-70 flex items-center gap-1">
                {aiSearching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} <span className="hidden md:inline">Ask AI</span>
              </button>
              {showResults && (searchResults.length > 0 || aiSearchResult) && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-popover text-popover-foreground rounded-xl shadow-2xl border border-border/50 max-h-96 overflow-auto z-[100]" data-testid="search-results" onMouseDown={e => e.preventDefault()}>
                  {/* AI Result */}
                  {aiSearchResult && (
                    <div className="p-3 border-b border-border/30 bg-indigo-500/5">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Sparkles className="w-3 h-3 text-indigo-400" />
                        <span className="text-[9px] font-medium text-indigo-400">AI Answer</span>
                        <span className="text-[8px] text-muted-foreground/70 ml-auto">{aiSearchResult.model_name}</span>
                      </div>
                      <p className="text-xs text-foreground">{aiSearchResult.result?.answer || 'No answer'}</p>
                      {aiSearchResult.result?.narrative && <p className="text-[10px] text-muted-foreground mt-1">{aiSearchResult.result.narrative}</p>}
                      <div className="flex gap-1.5 mt-2">
                        <button onClick={() => { sessionStorage.setItem('exploreAutoAsk', JSON.stringify({ model_id: aiSearchResult.model_id, question: searchQuery })); navigate('/ai-copilot'); setShowResults(false); setSearchQuery(''); setAiSearchResult(null); }} className="text-[9px] px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30">Open in Explore</button>
                        {aiSearchResult.result?.dimensions?.length > 0 && (
                          <button onClick={() => { sessionStorage.setItem('exploreAutoAsk', JSON.stringify({ model_id: aiSearchResult.model_id, question: searchQuery })); navigate('/ai-copilot'); setShowResults(false); setSearchQuery(''); setAiSearchResult(null); }} className="text-[9px] px-2 py-0.5 rounded bg-primary/20 text-primary hover:bg-primary/30">View Chart</button>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Regular search results */}
                  {searchResults.map((r, i) => {
                    const typeLabels = { report: 'Insight', glossary: 'Glossary', semantic_entity: 'Model', data_source: 'Source', schedule: 'Schedule', alert: 'Alert' };
                    return (
                    <button key={`${r.type}-${r.id}-${i}`} onClick={() => navigateToResult(r)} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/30 text-left transition-colors border-b border-border/20 last:border-0">
                      <span className="text-[9px] text-muted-foreground uppercase w-16 flex-shrink-0">{typeLabels[r.type] || r.type.replace('_', ' ')}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">{r.name}</p>
                        {r.description && <p className="text-[10px] text-muted-foreground truncate">{r.description}</p>}
                      </div>
                    </button>
                    );
                  })}
                  {searchResults.length === 0 && !aiSearchResult && searchQuery.length >= 2 && (
                    <div className="px-3 py-4 text-center">
                      <p className="text-xs text-muted-foreground">No results found</p>
                      <button onClick={handleAiSearch} className="text-[10px] text-indigo-400 mt-1 hover:underline flex items-center gap-1 mx-auto"><Sparkles className="w-3 h-3" /> Try asking AI</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(() => {
              const helpSection = resolveHelpSection(location.pathname);
              if (!helpSection) return null;
              return <HelpPopover section={helpSection} />;
            })()}
            <HealthStatusBadge />
            <WorkspaceSwitcher user={user} />
            <SoundToggle />
            {/* Theme picker — single Sun/Moon button until an extension
                registers extra themes; then it becomes a popover picker
                so the user can pick AMOLED / etc. directly. */}
            {availableThemes.length <= 2 ? (
              <Button
                data-testid="theme-toggle"
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                aria-label="Toggle theme"
                className="rounded-xl text-muted-foreground hover:text-foreground"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>
            ) : (
              <div className="relative" data-testid="theme-picker">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setThemeMenuOpen(o => !o)}
                  aria-label="Choose theme"
                  className="rounded-xl text-muted-foreground hover:text-foreground"
                  data-testid="theme-toggle"
                >
                  {theme === 'light' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </Button>
                {themeMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setThemeMenuOpen(false)} />
                    <div className="absolute right-0 mt-2 w-48 rounded-xl border border-border/40 bg-popover shadow-2xl p-1 z-50" data-testid="theme-menu">
                      <div className="px-2 py-1 text-[9px] uppercase tracking-wider text-muted-foreground">Theme</div>
                      {availableThemes.map(t => (
                        <button
                          key={t.name}
                          onClick={() => { setTheme(t.name); setThemeMenuOpen(false); }}
                          className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-xs transition-colors ${theme === t.name ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'}`}
                          data-testid={`theme-option-${t.name}`}
                        >
                          <span>{t.label}</span>
                          {t.source === 'extension' && <span className="text-[8px] uppercase tracking-wider text-emerald-400/70">Marketplace</span>}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            <Button
              data-testid="notifications-btn"
              variant="ghost"
              size="icon"
              onClick={() => setNotifOpen(!notifOpen)}
              aria-label="Notifications"
              className="rounded-xl text-muted-foreground hover:text-foreground relative"
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[9px] font-bold flex items-center justify-center leading-none" data-testid="notif-unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </Button>
            {notifOpen && (
              <div className="absolute right-0 top-12 w-80 bg-popover rounded-xl shadow-2xl border border-border/60 z-[100] max-h-80 overflow-auto" data-testid="notification-dropdown">
                <div className="flex items-center justify-between p-3 border-b border-border/30">
                  <span className="text-xs font-heading font-semibold text-foreground">Notifications</span>
                  {unreadCount > 0 && <button onClick={() => { api.markAllNotificationsRead().then(() => setNotifications(prev => prev.map(n => ({ ...n, read: true })))); }} className="text-[9px] text-primary">Mark all read</button>}
                </div>
                {notifications.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-6">No notifications</p>
                ) : notifications.slice(0, 15).map(n => (
                  <div key={n.notification_id} onClick={() => { if (!n.read) api.markNotificationRead(n.notification_id).then(() => setNotifications(prev => prev.map(nn => nn.notification_id === n.notification_id ? { ...nn, read: true } : nn))); if (n.link) navigate(n.link); setNotifOpen(false); }}
                    className={`px-3 py-2.5 border-b border-border/20 cursor-pointer hover:bg-muted/30 transition-colors ${n.read ? '' : 'bg-primary/5'}`}>
                    <p className={`text-xs ${n.read ? 'text-muted-foreground' : 'text-foreground font-medium'}`}>{n.title}</p>
                    <p className="text-[10px] text-muted-foreground/80 mt-0.5">{n.message?.slice(0, 80)}</p>
                    <p className="text-[8px] text-muted-foreground/70 mt-0.5">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 overflow-auto" data-collab-scroll>
          <AdminReturnBar />
          {(() => {
            const rp = permForPath(location.pathname);
            if (permsLoaded && rp && !hasPermission(rp)) {
              return (
                <div className="flex flex-col items-center justify-center h-full min-h-[60vh] text-center px-6" data-testid="access-denied">
                  <Lock className="w-10 h-10 text-muted-foreground/70 mb-4" strokeWidth={1.5} />
                  <h2 className="text-lg font-semibold text-foreground">You don't have access to this area</h2>
                  <p className="text-sm text-muted-foreground mt-1 max-w-sm">Your role doesn't include permission to view this page. Contact an administrator if you need access.</p>
                  <button onClick={() => navigate('/dashboard')} className="mt-5 px-4 py-2 rounded-lg bg-primary/15 text-primary text-sm font-medium hover:bg-primary/25 transition-colors" data-testid="access-denied-home">Go to Home</button>
                </div>
              );
            }
            return ws?.split ? <SplitView /> : <Outlet />;
          })()}
        </div>

        {/* Workbench — bottom dock of live working sessions + ⌘K switcher */}
        <WorkbenchDock />
      </main>

      <WorkbenchSwitcher />
      {floatingDiscussionId && (
        <DiscussionThreadDialog
          floating
          open
          discussionId={floatingDiscussionId}
          onOpenChange={(o) => { if (!o) setFloatingDiscussionId(null); }}
        />
      )}

      <Toaster position="top-right" richColors />

      <NavigationGuard />
      <FeedbackWidget />
      <PWAStatus />
      <CoachWidget />
      <StudioRecorderBar />
      <CollabOverlay />

      <ReleaseNotesDialog open={releaseOpen} onOpenChange={setReleaseOpen} currentVersion={liveVersion} buildDate={buildInfo?.build_date} />


      {/* Mobile Bottom Navigation */}
      <nav className="hidden max-md:flex mobile-bottom-nav bg-background/95 backdrop-blur-xl border-t border-border/50 items-center justify-around px-2 py-1">
        {[
          { path: '/dashboard', icon: LayoutDashboard, label: 'Home' },
          { path: '/ai-insights', icon: Lightbulb, label: 'Findings', termKey: 'ai_insights' },
          { path: '/dashboards', icon: FileText, label: 'Insights', termKey: 'insights' },
          { path: '/ai-copilot', icon: Search, label: 'AI Explore', termKey: 'ai_explore' },
          { path: '/kpi-monitor', icon: BarChart3, label: 'KPIs', termKey: 'kpi_monitor' },
          { path: '/visual-library', icon: Layers, label: 'Library', termKey: 'visual_library' },
        ].map(item => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <NavLink key={item.path} to={item.path} className={`flex flex-col items-center gap-0.5 py-1.5 px-3 rounded-lg transition-colors ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
              <Icon className="w-5 h-5" />
              <span className="text-[9px]">{navLabel(item)}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
